import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Headphones, Crown, ShoppingCart, ArrowRight, Check } from 'lucide-react';
import { useCart } from '../App';

const GOLD = '#f59e0b';
const GREEN = '#10b981';

const stronWord = (n) => {
  const u = n % 10, t = n % 100;
  if (n === 1) return 'strona';
  if (u >= 2 && u <= 4 && !(t >= 12 && t <= 14)) return 'strony';
  return 'stron';
};

const savingsPct = (p) =>
  p && p.regular_price > p.sale_price
    ? Math.round((p.regular_price - p.sale_price) / p.regular_price * 100)
    : 0;

const ProductFamilyCard = ({ group }) => {
  const { addToCart, cart } = useCart();
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null);
  const [err, setErr] = useState(false);

  const options = [
    { key: 'ebook', label: 'EBOOK', icon: BookOpen, product: group.ebook,
      detail: group.pages ? `${group.pages} ${stronWord(group.pages)}` : 'PDF' },
    { key: 'audiobook', label: 'AUDIOBOOK', icon: Headphones, product: group.audiobook,
      detail: group.audio_duration || 'MP3' },
    { key: 'premium', label: 'PREMIUM', icon: Crown, product: group.premium, highlight: true,
      detail: 'eBook + AudioBook' },
  ].filter(o => o.product);

  const heroProduct = (selected && group[selected]) || group.premium || group.audiobook || group.ebook;
  const coverUrl = heroProduct?.cover_image;

  const act = (buyNow) => {
    if (!selected) { setErr(true); return; }
    const product = group[selected];
    if (!product) return;
    if (!cart.some(i => i.id === product.id)) addToCart(product);
    if (buyNow) navigate('/checkout');
  };

  const selectedInCart = selected && cart.some(i => i.id === group[selected]?.id);

  return (
    <div className="glass-card card-glow p-5 sm:p-6 flex flex-col h-full">
      {/* Top badges */}
      {(group.badge || group.badge2) && (
        <div className="flex items-center justify-between gap-2 mb-3">
          {group.badge
            ? <span className="badge bg-orange-500/20 border-orange-500/50 text-orange-400 whitespace-nowrap">{group.badge}</span>
            : <span />}
          {group.badge2 && (
            <span className="badge bg-[#10b981]/15 border-[#10b981]/40 text-[#10b981] whitespace-nowrap">{group.badge2}</span>
          )}
        </div>
      )}

      {/* Title */}
      <h3 className="text-xl font-bold text-white leading-tight">{group.title}</h3>
      {group.subtitle && <p className="text-sm text-[#9CA3AF] mt-0.5">{group.subtitle}</p>}

      {/* Cover (left on desktop, top on mobile) + options */}
      <div className="flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-5 mt-4">
        <div className={`relative w-full sm:w-[46%] mx-auto sm:mx-0 flex-shrink-0 aspect-square sm:aspect-[3/4] rounded-xl flex items-center justify-center border border-[#10b981]/20 ${group.isPack ? 'p-2' : 'p-1'}`}
          style={{ background: 'radial-gradient(130% 110% at 50% 12%, rgba(16,185,129,0.20) 0%, rgba(16,185,129,0.11) 55%, rgba(10,26,22,0.60) 100%)' }}>
          {coverUrl && (
            <img src={coverUrl} alt={group.title}
              className="w-full h-full object-contain rounded-lg transition-all duration-300" />
          )}
        </div>

        <div className="flex-1 min-w-0 flex flex-col gap-2.5 justify-center">
          {/* Volume — centered, above the options */}
          {(group.pages > 0 || group.audio_duration) && (
            <div className="flex items-center justify-center gap-5 mb-1 text-sm text-[#9CA3AF] flex-wrap">
              {group.pages > 0 && (
                <span className="flex items-center gap-1.5"><BookOpen size={18} style={{ color: GREEN }} /> {group.pages} {stronWord(group.pages)}</span>
              )}
              {group.audio_duration && (
                <span className="flex items-center gap-1.5"><Headphones size={18} style={{ color: GREEN }} /> {group.audio_duration}</span>
              )}
            </div>
          )}

          {options.map(o => {
            const p = o.product;
            const isSel = selected === o.key;
            const accent = o.highlight ? GOLD : GREEN;
            const save = savingsPct(p);
            return (
              <button key={o.key} type="button"
                onClick={() => { setSelected(o.key); setErr(false); if (window.fbq) window.fbq('track', 'ViewContent', { value: Number(p.is_on_sale ? p.sale_price : p.regular_price) || 0, currency: 'PLN' }); }}
                className={`relative flex items-center gap-3 p-3.5 rounded-xl border text-left transition-all ${o.highlight ? 'mt-1.5' : ''} ${isSel ? '' : 'bg-[#0B0F0E] hover:border-white/25'}`}
                style={isSel
                  ? { borderColor: accent, background: accent + '14', boxShadow: `0 0 0 1px ${accent}` }
                  : { borderColor: o.highlight ? GOLD + '55' : 'rgba(255,255,255,0.10)' }}>
                {o.highlight && (
                  <span className="absolute -top-2.5 right-3 text-[11px] font-bold px-3 py-1 rounded-full whitespace-nowrap"
                    style={{ background: GOLD, color: '#0B0F0E' }}>BESTSELLER</span>
                )}
                <o.icon size={26} style={{ color: accent }} className="flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-white tracking-wide">{o.label}</div>
                  <div className="text-[11px] text-[#9CA3AF] leading-tight">{o.detail}</div>
                </div>
                <div className="text-right flex-shrink-0">
                  {p.regular_price > p.sale_price && (
                    <div className="text-[11px] text-[#6B7280] line-through mb-0.5" style={{ textDecorationThickness: '1px' }}>{p.regular_price} zł</div>
                  )}
                  <div className="text-lg font-extrabold leading-none" style={{ color: accent }}>{p.sale_price} zł</div>
                  {save > 0 && <div className="text-[9px] font-bold mt-1.5" style={{ color: GOLD }}>Oszczędzasz {save}%</div>}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Description + benefits */}
      <div className="mt-5 flex-grow">
        {group.description && <p className="text-sm text-[#9CA3AF]">{group.description}</p>}
        {group.benefits?.length > 0 && (
          <div className="space-y-2 mt-3">
            {group.benefits.map((b, i) => (
              <div key={i} className="flex items-start gap-2 text-sm">
                <Check size={16} className="text-[#10b981] mt-0.5 flex-shrink-0" />
                <span className="text-[#9CA3AF]">{b}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {err && <p className="text-red-400 text-sm mt-3 text-center">Najpierw wybierz opcję ↑</p>}

      {/* Buttons */}
      <div className="flex flex-col gap-2 mt-5">
        <button onClick={() => act(true)} className="btn-primary w-full" data-testid="buy-now-btn">
          Kup teraz <ArrowRight size={18} />
        </button>
        <button onClick={() => act(false)} className={`btn-ghost w-full ${selectedInCart ? 'opacity-70' : ''}`} data-testid="add-to-cart-btn">
          {selectedInCart ? <><Check size={18} /> Dodano do koszyka</> : <><ShoppingCart size={18} /> Dodaj do koszyka</>}
        </button>
      </div>
    </div>
  );
};

export default ProductFamilyCard;
